import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Text,
    Title,
    Paper,
    Select,
    Button,
    Loader,
    Group
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { FilterGroup } from './ScimFilterBuilder';
import { parseScimFilter } from './ScimFilterParser';
import { Phil } from './Phil';
import { useEntityTypes } from '../../contexts/EntityTypesContext';
import { getPluralizedLabel } from '../../utils/pluralizationUtils';
import { debugLog } from '../../utils/debugLog';

export interface ScimFilterConverterUIProps {
    fieldOptions: any[];
    onFilterConverted?: (filterGroup: FilterGroup) => void;
    onClear?: () => void;
}

export const ScimFilterConverterUI: React.FC<ScimFilterConverterUIProps> = ({
    fieldOptions: _fieldOptions,
    onFilterConverted,
    onClear: _onClear
}) => {
    // Store filter string outside Phil so it persists when switching entity types
    const [globalFilterString, setGlobalFilterString] = useState('');
    const [selectedEntityType, setSelectedEntityType] = useState<string>('Persons');

    // Get resource types and all discovered entity types from context
    const { allEntityTypes, isLoading: entityTypesLoading, refreshEntityTypes } = useEntityTypes();

    // Create entity type options from all discovered entity types with proper pluralization
    const entityTypeOptions = useMemo(() => {
        if (entityTypesLoading || !allEntityTypes.length) {
            return [];
        }

        const options = allEntityTypes.map(typeName => {
            // Keep the original singular entity type as the value
            // Use pluralized form only for display label
            const label = getPluralizedLabel(typeName);
            return {
                value: typeName, // Keep singular for internal use
                label: label     // Use pluralized for display
            };
        });

        return options;
    }, [allEntityTypes, entityTypesLoading]);

    // Update selected entity type when resource types are loaded (only if no valid selection exists)
    useEffect(() => {
        if (entityTypeOptions.length > 0) {
            // Check if the current selection is valid
            const isValidSelection = entityTypeOptions.some(option => option.value === selectedEntityType);

            if (!isValidSelection) {
                // Auto-select the first available option if current selection is invalid
                const firstOption = entityTypeOptions[0];
                if (firstOption) {
                    debugLog('🔍 ScimFilterConverterUI: Auto-selecting first entity type:', firstOption.value);
                    setSelectedEntityType(firstOption.value);
                }
            }
        }
    }, [entityTypeOptions, selectedEntityType]);

    // Build Select data: loading placeholder, options, or empty
    const selectData = useMemo(() => {
        if (entityTypesLoading) {
            return [{ value: '__loading__', label: 'Loading entity types...', disabled: true }];
        }
        if (entityTypeOptions.length === 0) {
            return [{ value: '__empty__', label: 'No entity types available', disabled: true }];
        }
        return entityTypeOptions;
    }, [entityTypesLoading, entityTypeOptions]);

    return (
        <Box p="xl" maw={1200} mx="auto">
            <Title order={1} mb="md">
                SCIM Filter Converter
            </Title>

            <Text size="sm" c="dimmed" mb="md">
                Select an entity type and build or type/paste SCIM filters. The filter string is preserved when switching between entity types.
            </Text>

            {/* Entity Type Selector */}
            <Paper withBorder p="lg" mb="lg">
                <Title order={3} mb="md">
                    Entity Type Selector
                </Title>

                <Group gap="md" align="center" mb="md">
                    <Select
                        label="Entity Type"
                        placeholder="Entity Type"
                        data={selectData}
                        value={entityTypesLoading ? '__loading__' : entityTypeOptions.length === 0 ? '__empty__' : selectedEntityType}
                        onChange={(val) => {
                            if (val && val !== '__loading__' && val !== '__empty__') {
                                debugLog('🔍 ScimFilterConverterUI: User selected entity type:', val);
                                setSelectedEntityType(val);
                            }
                        }}
                        disabled={entityTypesLoading}
                        miw={200}
                        rightSection={entityTypesLoading ? <Loader size={16} /> : undefined}
                    />

                    {/* Refresh Entity Types Button */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={refreshEntityTypes}
                        disabled={entityTypesLoading}
                        leftSection={<IconRefresh size={18} />}
                        title="Refresh discovered entity types from schemas endpoint"
                    >
                        Refresh Types
                    </Button>

                    {/* Entity Types Info */}
                    <Text size="xs" c="dimmed">
                        {entityTypeOptions.length > 0 ? `${entityTypeOptions.length} types discovered` : 'No types available'}
                    </Text>
                </Group>
            </Paper>

            {/* Phil Component */}
            <Phil
                entityType={selectedEntityType}
                initialFilter={globalFilterString}
                onFilterChange={(filterString) => {
                    setGlobalFilterString(filterString);
                    if (onFilterConverted) {
                        // Parse the filter string to FilterGroup for the callback
                        try {
                            const parsedFilter = parseScimFilter(filterString);
                            if (parsedFilter) {
                                onFilterConverted(parsedFilter);
                            }
                        } catch (error) {
                            console.warn('Failed to parse filter for callback:', error);
                        }
                    }
                }}
                showManualInput={true}
                showBuilder={true}
                showGeneratedFilter={true}
                compact={false}
                title={`Advanced Filter Builder for ${selectedEntityType}`}
            />
        </Box>
    );
};
